import { type CSSProperties, type ReactNode } from 'react';
import { Check, ChevronDown, FileText, Layers3, LoaderCircle } from 'lucide-react';
import './processing-workspace.css';

export type ProcessingWorkspaceProps = {
  title: string;
  stageId: string;
  stages: readonly { id: string; short: string; label: string }[];
  /**
   * The live line: what the engine is doing right now in plain words
   * ("Reading page 14 of 106", "Choosing useful fields for Invoice") and the
   * time on that step. Never a file name: the one current document is named
   * once, under its page.
   */
  live?: { text: string } | null;
  progress: {
    headline: string;
    detail: string;
    total?: number;
    completed?: number;
    /** 0..1 fill that never slides back within a stage; defaults to completed / total. */
    bar?: number;
    valueText: string;
    indeterminate: boolean;
    /** The headline names the step being done (a setup step), not a count: drawn smaller, with a plain bar. */
    named?: boolean;
  };
  /** Kept for callers; the facts carry elapsed and time left. */
  elapsed?: string;
  remaining?: string | null;
  /** Facts from the same progress state as the headline: a fixed set, always visible. */
  metrics: readonly { label: string; value: string }[];
  preview: ReactNode;
  previewLabel: string;
  pageNumber?: number;
  types: readonly { name: string; fields: string[]; samples: number; rows: number }[];
  pendingTypes: readonly string[];
  reportedTypeCount: number;
  findingsTitle?: string;
  emptyInsight: ReactNode;
  /**
   * What a step has found, drawn in place of the type cards while it is the
   * story (the Read step: words, barcodes, the latest finds). Types come after
   * the pages are read, so nothing is hidden by it.
   */
  finds?: ReactNode;
  technicalDetails?: ReactNode;
  sampleAction?: ReactNode;
  processingAction?: ReactNode;
  /** What comes next, one small card per stage (the setup wait that teaches). */
  nextCard?: ReactNode;
  /** Processing details: totals from the same state as everything else. */
  details?: readonly { label: string; value: string }[];
  /** The type whose fields are being chosen right now, drawn as in progress. */
  activeType?: string | null;
  /**
   * PAIGE: each type's field design while it runs (setup-design-progress.ts): every type
   * being asked about drawn as in progress, its questions answered and the fields found so far.
   */
  typeProgress?: Readonly<
    Record<string, { working: boolean; status: string; detail: string; title: string }>
  >;
  /** False once the work has finished or is held; stops the live motion. */
  working?: boolean;
  className?: string;
  /**
   * Which part of the job's journey this screen is (data-phase), and the part
   * already finished, named above the heading. Setting up a job and processing
   * its documents run the same five steps; without a phase of its own the
   * import's step list read as setup going back to Prepare (owner, 2026-09-29).
   */
  phase?: { id: string; finished?: string };
};

const countLabel = (value: number, singular: string, plural = `${singular}s`) =>
  `${value.toLocaleString()} ${value === 1 ? singular : plural}`;
const validCount = (value: number) => Number.isSafeInteger(value) && value >= 0;

/**
 * Presentation only. The caller owns work, preview identity and every count,
 * and draws them all from ONE progress state (working-progress.ts): the step
 * list, the headline count, the live line, the facts and the page caption can
 * therefore never disagree. Nothing here remembers a stage of its own; a
 * component that outlived its run used to carry the last run's "Finish".
 */
export function ProcessingWorkspace({
  title,
  stageId,
  stages,
  live,
  progress,
  metrics,
  preview,
  previewLabel,
  pageNumber,
  types,
  pendingTypes,
  reportedTypeCount,
  findingsTitle = 'Found so far',
  emptyInsight,
  finds,
  technicalDetails,
  sampleAction,
  processingAction,
  nextCard,
  activeType,
  typeProgress,
  details,
  working = true,
  className,
  phase,
}: ProcessingWorkspaceProps) {
  // "complete": every step done, none current.
  const currentStage =
    stageId === 'complete' ? stages.length : stages.findIndex((stage) => stage.id === stageId);
  const { total, completed } = progress;
  const counted =
    typeof total === 'number' &&
    Number.isFinite(total) &&
    total > 0 &&
    typeof completed === 'number' &&
    Number.isFinite(completed) &&
    completed >= 0 &&
    completed <= total;
  // A named step has a plain bar of its own fraction and no count on the track.
  const barOnly = !counted && typeof progress.bar === 'number' && Number.isFinite(progress.bar);
  const measured = !progress.indeterminate && (counted || barOnly);
  const amount = measured ? Math.min(1, Math.max(0, progress.bar ?? completed! / total!)) : null;
  const typeCountKnown = validCount(reportedTypeCount);
  const designing = (name: string) => name === activeType || typeProgress?.[name]?.working === true;
  const shownNames = new Set([...types.map((type) => type.name), ...pendingTypes]);

  return (
    <section
      className={`processing-workspace${working ? ' processing-workspace-working' : ''}${className ? ` ${className}` : ''}`}
      aria-label={title}
      data-phase={phase?.id}
    >
      <ol className="processing-workspace-stages" aria-label={`${title}: steps`}>
        {stages.map((stage, index) => {
          const done = currentStage >= 0 && index < currentStage;
          return (
            <li
              key={stage.id}
              aria-current={index === currentStage ? 'step' : undefined}
              className={
                index === currentStage
                  ? 'processing-stage-current'
                  : done
                    ? 'processing-stage-past'
                    : undefined
              }
            >
              <span className="processing-stage-number" aria-hidden="true">
                {done ? <Check size={15} strokeWidth={2.6} /> : index + 1}
              </span>
              <span>
                {stage.short}
                {done && <span className="visually-hidden"> (done)</span>}
              </span>
            </li>
          );
        })}
      </ol>

      <header className="processing-workspace-heading">
        <div className="processing-workspace-heading-copy">
          {phase?.finished && (
            <p className="processing-workspace-phase">
              <Check size={14} strokeWidth={2.6} aria-hidden="true" />
              <span>{phase.finished}</span>
            </p>
          )}
          <h2>{title}</h2>
          {processingAction && (
            <div className="processing-workspace-action">{processingAction}</div>
          )}
        </div>
        <div
          className={`processing-workspace-progress${progress.named ? ' processing-workspace-progress-named' : ''}`}
          role="status"
          aria-label={progress.valueText}
          aria-live="polite"
          aria-atomic="true"
        >
          <strong>{progress.headline}</strong>
          {progress.detail && <p>{progress.detail}</p>}
        </div>
        <div
          className={`processing-workspace-progress-track${measured ? '' : ' processing-workspace-progress-indeterminate'}${working ? ' processing-workspace-progress-live' : ''}`}
          role="progressbar"
          aria-label={title}
          aria-valuemin={0}
          aria-valuemax={measured ? (counted ? total : 100) : undefined}
          aria-valuenow={measured ? (counted ? completed : Math.round(amount! * 100)) : undefined}
          aria-valuetext={progress.valueText}
        >
          {/* Keyed by stage: a new stage's bar starts empty instead of sliding back from the last one's. */}
          <span
            key={stageId}
            style={measured ? ({ width: `${amount! * 100}%` } as CSSProperties) : undefined}
          />
        </div>
        {live?.text && (
          <p className="processing-workspace-live" aria-live="off">
            {working && <span className="processing-workspace-pulse" aria-hidden="true" />}
            <span className="processing-workspace-live-text">{live.text}</span>
          </p>
        )}
        {metrics.length > 0 && (
          <dl className="processing-workspace-facts">
            {metrics.map((metric, index) => (
              <div key={`${metric.label}:${index}`}>
                <dt>{metric.label}</dt>
                <dd>{metric.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {nextCard}
        {sampleAction}
      </header>

      <div className="processing-workspace-body">
        <figure className="processing-workspace-paper">
          <div className="processing-workspace-preview">{preview}</div>
          {/* The one place the current document is named; nothing until a page exists. */}
          {previewLabel && (
            <figcaption>
              <span title={previewLabel}>
                <FileText size={16} aria-hidden="true" />
                <span>{previewLabel}</span>
              </span>
              {typeof pageNumber === 'number' &&
                Number.isSafeInteger(pageNumber) &&
                pageNumber > 0 && <strong>Page {pageNumber.toLocaleString()}</strong>}
            </figcaption>
          )}
        </figure>

        {finds ? (
          <section
            className="processing-workspace-findings processing-workspace-finds"
            aria-label="Found while reading"
          >
            {finds}
          </section>
        ) : (
          <section className="processing-workspace-findings" aria-label={findingsTitle}>
            {(shownNames.size > 0 || (typeCountKnown && reportedTypeCount > 0)) && (
              <header>
                <div>
                  <Layers3 size={20} aria-hidden="true" />
                  <h3>{findingsTitle}</h3>
                </div>
                <p className="processing-workspace-type-count">
                  {typeCountKnown ? (
                    <>
                      <strong>{reportedTypeCount.toLocaleString()}</strong>
                      <span>{reportedTypeCount === 1 ? 'document type' : 'document types'}</span>
                    </>
                  ) : (
                    <span>Type count unavailable</span>
                  )}
                </p>
                {typeCountKnown && shownNames.size < reportedTypeCount && (
                  <small>
                    Showing {shownNames.size.toLocaleString()} of{' '}
                    {reportedTypeCount.toLocaleString()}
                  </small>
                )}
              </header>
            )}
            <div
              className="processing-workspace-finding-list"
              tabIndex={0}
              role="region"
              aria-label="Document types and fields found so far"
            >
              {/* Compact, equal chips: name and field count. */}
              {types.map((type) => (
                <article
                  key={type.name}
                  className={`processing-workspace-type${designing(type.name) ? ' processing-workspace-type-active' : ''}`}
                >
                  <h4>{type.name}</h4>
                  <p className="processing-workspace-type-meta">
                    {type.fields.length > 0 ? (
                      <span>{countLabel(type.fields.length, 'field')}</span>
                    ) : (
                      <span>Fields next</span>
                    )}
                    {validCount(type.samples) && type.samples > 0 && (
                      <span>{countLabel(type.samples, 'sample')}</span>
                    )}
                  </p>
                </article>
              ))}
              {pendingTypes.map((name) => {
                const design = typeProgress?.[name];
                return (
                  <article
                    key={`pending:${name}`}
                    title={design?.title}
                    className={`processing-workspace-type processing-workspace-type-pending${designing(name) ? ' processing-workspace-type-active' : ''}${design ? ' processing-workspace-type-designing' : ''}`}
                  >
                    <h4>{name}</h4>
                    <p className="processing-workspace-type-meta">
                      {designing(name) ? (
                        <span className="processing-workspace-type-working">
                          <LoaderCircle size={13} className="spin" aria-hidden="true" />
                          {design?.status ?? 'Choosing fields'}
                        </span>
                      ) : (
                        <span>{design?.status ?? 'Fields next'}</span>
                      )}
                    </p>
                    {design?.detail && (
                      <p className="processing-workspace-type-found">{design.detail}</p>
                    )}
                  </article>
                );
              })}
              {!types.length && !pendingTypes.length && (
                <div className="processing-workspace-empty">{emptyInsight}</div>
              )}
            </div>
          </section>
        )}
      </div>

      {(details?.length || technicalDetails) && (
        <details className="processing-workspace-details">
          <summary>
            <ChevronDown size={16} aria-hidden="true" />
            Processing details
          </summary>
          {/* Opens over the findings, inside the same frame: the page never scrolls. */}
          <div className="processing-workspace-details-body">
            {details && details.length > 0 && (
              <dl className="processing-workspace-metrics">
                {details.map((item) => (
                  <div key={item.label}>
                    <dt>{item.label}</dt>
                    <dd>{item.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {technicalDetails && (
              <div className="processing-workspace-technical">{technicalDetails}</div>
            )}
          </div>
        </details>
      )}
    </section>
  );
}

export default ProcessingWorkspace;
